import React from 'react';

import Box from '@mui/material/Box';
import { Capacitor } from '@capacitor/core';
import { alpha, type Theme, type SxProps } from '@mui/material/styles';
import mergeSxProps from '../shared/mergeSxProps';

export type ModifierKeys = 'Meta' | 'Shift' | 'Alt' | 'Control';
export type SpecialKeys =
  | 'Enter'
  | 'Escape'
  | 'Backspace'
  | 'Tab'
  | 'Delete'
  | 'ArrowUp'
  | 'ArrowDown'
  | 'ArrowLeft'
  | 'ArrowRight';

export type KeyShortcutProps = {
  eventKey: string;
  modifiers?: ModifierKeys[];
  before?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

/** Renders a keyboard hint chip for dialog shortcuts. */
export default function KeyShortcut(props: KeyShortcutProps) {
  'use memo';

  const { eventKey, modifiers, before, disabled, sx } = props;

  const modifiersString = modifiers
    ?.map((modifier) => {
      switch (modifier) {
        case 'Meta':
          return '⌘';
        case 'Shift':
          return '⇧';
        case 'Alt':
          return '⌥';
        case 'Control':
          return '⌃';
      }
    })
    .join('');

  const eventKeyString = ((key: string) => {
    switch (key) {
      case 'Enter':
        return '⮐';
      case 'Escape':
        return 'ESC';
      case 'Backspace':
        return '⌫';
      case 'Tab':
        return '⇥';
      case 'Delete':
        return '⌦';
      case 'ArrowUp':
        return '↑';
      case 'ArrowDown':
        return '↓';
      case 'ArrowLeft':
        return '←';
      case 'ArrowRight':
        return '→';
      default:
        return key;
    }
  })(eventKey);

  const modifiersFontSize = '70%';

  const eventKeyFontSize = eventKey === 'Enter' ? '50%' : '70%';

  if (Capacitor.isNativePlatform()) {
    return null;
  }

  return (
    <Box
      className="key-shortcut"
      sx={mergeSxProps(
        (theme) => ({
          backgroundColor: alpha(theme.palette.customColors.opposite, 0.7),
          border: `1px solid ${alpha(theme.palette.customColors.main, 0.2)}`,
          color: theme.palette.customColors.main,
          textTransform: 'uppercase',
          minWidth: '1.2rem',
          px: 0.5,
          ml: before ? 0 : 2,
          mr: before ? 2 : 0,
          borderRadius: 1,
          height: '1.3rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? theme.palette.action.disabledOpacity : 1,
          transition: theme.transitions.create(['opacity']),
        }),
        sx,
      )}
    >
      <span style={{ fontSize: modifiersFontSize }}>{modifiersString}</span>
      <span style={{ fontSize: eventKeyFontSize }}>{eventKeyString}</span>
    </Box>
  );
}
